import React, { useState } from 'react';
import { Plus, Archive, ArchiveRestore } from 'lucide-react';
import {
  BrioModal, BrioModalBody, BrioInput, BrioButton,
} from '../../components/BrioComponents';

// Manage the BOQ building-typology vocabulary: add custom labels, rename custom
// ones, archive/restore. Seeded typologies (is_custom=false, from Accounting ->
// Categories) can be archived but not renamed here — rename those on the
// Categories page so the firm-wide list stays the source of truth.
function ManageTypologiesModal({ api, onClose }) {
  const { typologies, createTypology, updateTypology } = api;
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const add = async () => {
    if (!newLabel.trim()) return;
    setBusy(true); setError(null);
    const res = await createTypology(newLabel);
    setBusy(false);
    if (res.success) setNewLabel('');
    else setError(res.error || 'Could not add typology.');
  };

  const toggleActive = async (t) => {
    setBusy(true); setError(null);
    const res = await updateTypology(t.id, { isActive: !t.isActive });
    setBusy(false);
    if (!res.success) setError(res.error || 'Could not update typology.');
  };

  const cell = { padding: '0.4rem 0.5rem', fontSize: '0.85rem', borderBottom: '1px solid var(--brio-border-subtle, #eee)' };

  return (
    <BrioModal title="Manage building typologies" onClose={onClose} width="sm">
      <BrioModalBody>
        {error && (
          <div style={{ padding: '0.6rem 0.9rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '0.8rem' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <BrioInput
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="New custom typology (e.g. Equestrian)"
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            style={{ flex: 1 }}
          />
          <BrioButton onClick={add} disabled={busy || !newLabel.trim()}>
            <Plus size={15} style={{ marginRight: 4, verticalAlign: '-2px' }} />Add
          </BrioButton>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {typologies.map((t) => (
              <tr key={t.id} style={{ opacity: t.isActive ? 1 : 0.5 }}>
                <td style={{ ...cell, fontWeight: 600 }}>{t.label}</td>
                <td style={{ ...cell, color: 'var(--brio-text-muted)', fontSize: '0.72rem' }}>
                  {t.isCustom ? 'custom' : 'from Categories'}
                </td>
                <td style={{ ...cell, textAlign: 'right' }}>
                  <button
                    type="button"
                    title={t.isActive ? 'Archive' : 'Restore'}
                    onClick={() => toggleActive(t)}
                    disabled={busy}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--brio-text-muted)' }}
                  >
                    {t.isActive ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: '0.9rem', fontSize: '0.72rem', color: 'var(--brio-text-muted)' }}>
          Standard typologies are seeded from Accounting → Categories. Add custom ones here for
          zone-only types (Equestrian, Sports…) that aren’t firm-wide project categories.
        </div>
      </BrioModalBody>
    </BrioModal>
  );
}

export default ManageTypologiesModal;
